import api from '../api';

/**
 * Elliott Wave Analysis API service
 * Connects to /api/v1/elliott-wave/ backend endpoints
 */

/**
 * Run Elliott Wave analysis on a symbol (GET)
 * @param {string} symbol - Stock/index symbol (e.g., 'NIFTY50', 'RELIANCE')
 * @param {string} timeframe - '5min' | '15min' | '1hour' | '1day'
 * @param {boolean} includeMtf - Include multi-timeframe analysis
 * @returns {Promise} ElliottWaveAnalysisResponse
 */
export const analyzeSymbol = async (symbol, timeframe = '5min', includeMtf = true) => {
  const response = await api.get(`/api/v1/elliott-wave/analyze/${encodeURIComponent(symbol)}`, {
    params: { timeframe, include_mtf: includeMtf },
    timeout: 120000, // 2 minutes - analysis can be slow
  });
  return response.data;
};

/**
 * Run Elliott Wave analysis on a symbol (POST)
 * @param {Object} params
 * @param {string} params.symbol - Stock/index symbol
 * @param {string} params.timeframe - '5min' | '15min' | '1hour' | '1day'
 * @param {boolean} params.include_mtf - Include multi-timeframe analysis
 * @param {number|null} params.lookback_bars - Number of bars to analyze
 * @returns {Promise} ElliottWaveAnalysisResponse
 */
export const analyzeSymbolPost = async ({ symbol, timeframe = '5min', include_mtf = true, lookback_bars = null }) => {
  const response = await api.post('/api/v1/elliott-wave/analyze', {
    symbol,
    timeframe,
    include_mtf,
    lookback_bars,
  }, {
    timeout: 120000,
  });
  return response.data;
};

/**
 * Get analysis history for a symbol
 * @param {string} symbol - Stock/index symbol
 * @param {string|null} timeframe - Optional timeframe filter
 * @param {number} limit - Max records (1-100)
 * @returns {Promise} Array of WavePatternResponse
 */
export const getAnalysisHistory = async (symbol, timeframe = null, limit = 10) => {
  const params = { limit };
  if (timeframe) params.timeframe = timeframe;
  
  const response = await api.get(`/api/v1/elliott-wave/history/${encodeURIComponent(symbol)}`, {
    params,
    timeout: 30000,
  });
  return response.data;
};

/**
 * Get recent analyses across all symbols
 * @param {number} limit - Max records (1-100)
 * @returns {Promise} Array of recent analyses
 */
export const getRecentAnalyses = async (limit = 20) => {
  const response = await api.get('/api/v1/elliott-wave/recent-analyses', {
    params: { limit },
    timeout: 30000,
  });
  return response.data;
};

const elliottWaveApi = {
  analyzeSymbol,
  analyzeSymbolPost,
  getAnalysisHistory,
  getRecentAnalyses,
};

export default elliottWaveApi;
